import { SearchIcon, TimeIcon } from "@chakra-ui/icons";
import {
  Badge,
  Box,
  Button,
  Center,
  Flex,
  Heading,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Spinner,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useContext, useState } from "react";
import { FaCalendarPlus } from "react-icons/fa";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { getAppointmentsApi } from "../../api/appointments";
import LayoutView from "../../components/layout";
import Appointment from "../../components/navbar/sections/appointment";
import Auth from "../../hoc/Auth";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import { GlobalContext } from "../../context/Provider";
import { PERMISSIONS } from "../../auth/permissions";

const Appointments = () => {
  const navigate = useNavigate();
  const appointmentModal = useDisclosure();
  const [searchText, setSearchText] = useState("");
  const { can } = useContext(GlobalContext);
  const debouncedSearch = useDebouncedValue(searchText);
  const appointmentQuery = useQuery(
    ["getAppointmentsApi", debouncedSearch],
    () => getAppointmentsApi(1, 20, debouncedSearch),
    { keepPreviousData: true },
  );
  const appointments = appointmentQuery.data?.data?.appointments || [];

  return (
    <LayoutView activePage="Attendances">
      <Box maxW="1200px" mx="auto">
        <Flex
          justify="space-between"
          align={{ base: "stretch", md: "flex-end" }}
          direction={{ base: "column", md: "row" }}
          gap="4"
        >
          <Box>
            <Heading size="md">Appointment queue</Heading>
            <Text mt="1" color="gray.500">
              Review active consultations and completed visits.
            </Text>
          </Box>
          {can(PERMISSIONS.PATIENTS_VIEW) && (
            <Button
              leftIcon={<FaCalendarPlus />}
              onClick={appointmentModal.onOpen}
            >
              Create appointment
            </Button>
          )}
        </Flex>

        <Box
          mt="6"
          bg="white"
          borderRadius="2xl"
          boxShadow="card"
          p={{ base: "4", md: "6" }}
        >
          <InputGroup maxW="480px" size="lg">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.400" />
            </InputLeftElement>
            <Input
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              placeholder="Search patient ID or name"
              aria-label="Search appointments"
            />
          </InputGroup>

          {appointmentQuery.isLoading ? (
            <Center minH="320px">
              <Spinner color="brand.500" size="lg" />
            </Center>
          ) : appointmentQuery.isError ? (
            <Center minH="320px" flexDirection="column" textAlign="center">
              <Text fontWeight="700">Unable to load appointments</Text>
              <Button
                mt="4"
                variant="outline"
                onClick={() => appointmentQuery.refetch()}
              >
                Try again
              </Button>
            </Center>
          ) : !appointments.length ? (
            <Center minH="320px" flexDirection="column" textAlign="center">
              <Center
                w="56px"
                h="56px"
                borderRadius="full"
                bg="brand.50"
                color="brand.600"
              >
                <TimeIcon boxSize="6" />
              </Center>
              <Text mt="4" fontWeight="700">
                No appointments found
              </Text>
              <Text mt="1" color="gray.500">
                {searchText
                  ? "Try a different search term."
                  : "The appointment queue is currently empty."}
              </Text>
            </Center>
          ) : (
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing="5" mt="6">
              {appointments.map((appointment) => {
                const patient = appointment.patient;
                return (
                  <Box
                    key={appointment._id}
                    borderWidth="1px"
                    borderColor="gray.100"
                    borderRadius="xl"
                    p="5"
                    _hover={{
                      borderColor: "brand.200",
                      boxShadow: "md",
                      transform: "translateY(-2px)",
                    }}
                    transition="all .18s ease"
                  >
                    <Flex justify="space-between" gap="4" align="flex-start">
                      <Box minW="0">
                        <Text
                          fontWeight="800"
                          fontSize="lg"
                          textTransform="capitalize"
                          noOfLines={1}
                        >
                          {patient?.bioData?.surname}{" "}
                          {patient?.bioData?.otherNames}
                        </Text>
                        <Text mt="1" fontSize="sm" color="gray.500">
                          Patient ID:{" "}
                          {patient?.patientNumber ||
                            patient?._id?.slice(-6) ||
                            "—"}
                        </Text>
                      </Box>
                      <Badge
                        colorScheme={appointment.closed ? "blue" : "green"}
                        borderRadius="full"
                        px="3"
                        py="1"
                      >
                        {appointment.closed ? "Completed" : "In queue"}
                      </Badge>
                    </Flex>
                    <Flex mt="6" justify="space-between" align="center" gap="3">
                      <Box>
                        <Text
                          fontSize="xs"
                          color="gray.400"
                          textTransform="uppercase"
                          fontWeight="700"
                        >
                          Created
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                          {new Date(appointment.timestamp).toLocaleDateString(
                            undefined,
                            { day: "numeric", month: "short", year: "numeric" },
                          )}
                        </Text>
                      </Box>
                      <Button
                        size="sm"
                        variant={appointment.closed ? "outline" : "solid"}
                        onClick={() =>
                          navigate(`/appointment/${appointment._id}`)
                        }
                      >
                        {appointment.closed ? "View details" : "Attend patient"}
                      </Button>
                    </Flex>
                  </Box>
                );
              })}
            </SimpleGrid>
          )}
        </Box>
      </Box>
      <Appointment appointmentModal={appointmentModal} />
    </LayoutView>
  );
};

export default Auth(Appointments);
